import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { IS_FEATURE_ENABLED_QUERY } from '../graphql';
import { POLICY_FLAG } from '../config/policies';

const head = { fontSize: '10px', fontWeight: 700, color: 'var(--text)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' };
const linkStyle = { fontSize: '12px', color: 'var(--text-muted)', textDecoration: 'none', transition: 'color .12s' };
const hoverIn = (e) => { e.currentTarget.style.color = 'var(--primary)'; };
const hoverOut = (e) => { e.currentTarget.style.color = 'var(--text-muted)'; };

function Col({ title, links }) {
  return (
    <div>
      <p style={head}>{title}</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {links.map((l) => (
          <li key={l.label}>
            {l.href ? (
              <a href={l.href} target={l.external ? '_blank' : undefined} rel={l.external ? 'noopener noreferrer' : undefined}
                style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                {l.label}{l.external && <span style={{ fontSize: '11px', opacity: 0.7 }}> ↗</span>}
              </a>
            ) : (
              <Link to={l.to} style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>{l.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SiteFooter() {
  // Same fail-open rule as the pages themselves: only an explicit false hides the links, so a slow
  // or unreachable API never silently removes the policy links a gateway review looks for.
  const { data: policyFlag } = useQuery(IS_FEATURE_ENABLED_QUERY, {
    variables: { key: POLICY_FLAG }, fetchPolicy: 'cache-first', errorPolicy: 'ignore',
  });
  const showPolicies = policyFlag?.isFeatureEnabled !== false;
  return (
    <footer style={{ borderTop: '1px solid var(--border)', marginTop: '40px', background: 'var(--surface)' }}>
      <div className="footer-grid"
        style={{ maxWidth: '1100px', margin: '0 auto', padding: '28px 20px 20px', display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: '18px 28px' }}>

        {/* Col 1 — Brand */}
        <div>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', marginBottom: '10px' }}>
            <svg width="34" height="34" viewBox="0 0 64 64" fill="none" aria-hidden="true">
              <rect width="64" height="64" rx="16" fill="url(#footg)"/>
              <circle cx="32" cy="32" r="8.5" fill="white"/>
              <circle cx="32" cy="11" r="5.2" fill="white" opacity="0.92"/>
              <circle cx="50" cy="21.5" r="5.2" fill="white" opacity="0.92"/>
              <circle cx="50" cy="42.5" r="5.2" fill="white" opacity="0.92"/>
              <circle cx="32" cy="53" r="5.2" fill="white" opacity="0.92"/>
              <circle cx="14" cy="42.5" r="5.2" fill="white" opacity="0.92"/>
              <circle cx="14" cy="21.5" r="5.2" fill="white" opacity="0.92"/>
              <line x1="32" y1="23.5" x2="32" y2="16.2" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <line x1="39.4" y1="27.5" x2="45.2" y2="24.4" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <line x1="39.4" y1="36.5" x2="45.2" y2="39.6" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <line x1="32" y1="40.5" x2="32" y2="47.8" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <line x1="24.6" y1="36.5" x2="18.8" y2="39.6" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <line x1="24.6" y1="27.5" x2="18.8" y2="24.4" stroke="white" strokeWidth="2.8" opacity="0.5"/>
              <defs>
                <linearGradient id="footg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#2d6049"/>
                  <stop offset="1" stopColor="#38785A"/>
                </linearGradient>
              </defs>
            </svg>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontWeight: 500 }}>a QuantAISeed product</div>
              <div style={{ fontSize: '17px', fontWeight: 800, background: 'linear-gradient(90deg,#2d6049,#38785A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Ledxo Hub</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>enabling community</div>
              <div style={{ fontSize: '10px', color: '#38785A', fontWeight: 600 }}>hub.ledxo.com</div>
            </div>
          </Link>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '10px', maxWidth: '260px' }}>
            Enabling community — India-first platform for local groups, experts, events, and commerce. Built by{' '}
            <a href="https://quantaiseed.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>QuantAISeed Technology</a>.
          </p>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'var(--primary-light)', color: 'var(--primary-hover)', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--primary)' }}>
            🇮🇳 India-first
          </span>
        </div>

        {/* Col 2 — Explore */}
        <Col title="Explore" links={[
          { label: 'Food Market', to: '/food' },
          { label: 'Communities', to: '/' },
          { label: 'Social feed', to: '/social' },
          { label: 'Events', to: '/events' },
        ]} />

        {/* Col 3 — Earn & Sell */}
        <Col title="Earn & Sell" links={[
          { label: 'Sell on Ledxo Hub', to: '/seller' },
          { label: 'Become an Expert', to: '/expert' },
          { label: 'Post a Need', to: '/requests' },
          { label: 'My Orders & Account', to: '/orders' },
        ]} />

        {/* Col 4 — Company & Help. The policy links are not decoration: a payment gateway reviewer
            checks they are reachable by browsing the site, not only by opening a submitted URL. */}
        <Col title="Company & Help" links={[
          { label: 'About QuantAISeed', href: 'https://quantaiseed.com/', external: true },
          { label: 'Privacy & Data Rights', to: '/privacy' },
          ...(showPolicies ? [
            { label: 'Terms & Conditions', to: '/terms' },
            { label: 'Refund & Cancellation', to: '/refund-policy' },
            { label: 'Return Policy', to: '/return-policy' },
            { label: 'Shipping Policy', to: '/shipping-policy' },
          ] : []),
          { label: 'contact@quantaiseed.com', href: 'mailto:contact@quantaiseed.com' },
        ]} />
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
            © 2026{' '}
            <a href="https://quantaiseed.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'none', fontWeight: 700 }} onMouseEnter={hoverIn} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text)'; }}>QuantAISeed Technology</a>{' '}
            · hub.ledxo.com ·{' '}
            <a href="mailto:contact@quantaiseed.com" style={{ color: 'var(--text-muted)', textDecoration: 'none' }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>contact@quantaiseed.com</a>
          </p>
          <div style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
            <Link to="/privacy" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>Privacy</Link>
            {showPolicies && <Link to="/terms" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>Terms</Link>}
            {showPolicies && <Link to="/refund-policy" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>Refunds</Link>}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 18px 16px !important; }
        }
        @media (max-width: 440px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </footer>
  );
}
